import {
  DlMuiTypography,
  GetStylesFunction,
  DlMuiRadioGroup,
  enumContainer,
  ConfigObjectType,
  useApiMutation,
  useChangedByOtherDialog,
  CHANGED_BY_OTHER_USER_STATUS,
} from '@wallaby-ui/core';
import * as React from 'react';
import {useState, useMemo} from 'react';
import {useStore} from 'react-redux';
import {useParams} from 'react-router-dom';
import {useFormatters} from 'src/js/common';
import {MerieSchema} from 'src/js/common/types';

import {EditableField} from './EditableField';
import {EditableFieldWrapper} from './EditableFieldWrapper';
import {EditIconWrapper} from './EditIconWrapper';
import {usePermissions} from '../../permissions/usePermissions';

interface OwnProps {
  patientCardEtag: string | undefined;
  data: MerieSchema.components['schemas']['PatientCardDto'];
  reloadPatientCard: () => void;
  config: ConfigObjectType;
}

type Props = OwnProps;

export const AtRiskEdit: React.FC<Props> = (props) => {
  const {patientCardEtag, data, reloadPatientCard, config, ...restProps} = props;
  const state = useStore()?.getState();
  const params = useParams();
  const enumMap = enumContainer.getEnumMap(state);
  const {t, tEnum} = useFormatters();

  const [showEditButtonRisk, setShowEditButtonRisk] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [atRiskRadioValue, setAtRiskRadioValue] = useState<string>(data.atRiskState);

  const handleOnMouseEnterRisk = () => {
    setShowEditButtonRisk(true);
  };
  const handleOnMouseLeaveRisk = () => {
    setShowEditButtonRisk(false);
  };

  const handleOnClickRisk = () => {
    setIsEditing(true);
  };

  const handleDiscardRisk = () => {
    setIsEditing(false);
    setAtRiskRadioValue(data.atRiskState);
  };

  const riskSeverityApiUrl = `${config.service.url.hospitalizationDetail}/${params.hospitalizationId}/patient-card/risk-severity`;
  const atRiskRadioGroupEnum = enumMap.RiskSeverityEnum;

  const atRiskRadioGroupOptions = useMemo(
    () =>
      atRiskRadioGroupEnum
        .filter((v) => v.name !== 'UNCATEGORIZED')
        .map((v) => ({
          value: v.name,
          translatedLabel: tEnum('RiskSeverityEnum', v.name),
        })),
    [atRiskRadioGroupEnum, tEnum]
  );

  const onSuccess = async () => {
    reloadPatientCard();
  };
  const {changedByOtherDialog, showChangedByOtherDialog} = useChangedByOtherDialog({
    handleConfirm: () => {
      reloadPatientCard();
    },
  });
  const onError = async (error) => {
    if (error?.response?.status === CHANGED_BY_OTHER_USER_STATUS)
      showChangedByOtherDialog({
        [t('patient.card.atRisk')]: tEnum('RiskSeverityEnum', atRiskRadioValue),
      });
    else reloadPatientCard();
  };

  const {mutate: riskSeverityMutate} = useApiMutation(
    [riskSeverityApiUrl, patientCardEtag],
    {riskSeverity: atRiskRadioValue},
    {
      reactMutationOptions: {
        onSuccess,
        onError,
      },
      axiosConfig: {
        data: {riskSeverity: atRiskRadioValue},
        method: 'PATCH',
        url: riskSeverityApiUrl,
        headers: {
          'If-Match': patientCardEtag,
        },
      },
    },
    true
  );

  const handleOnSubmitRisk = () => {
    // always mutate to check etag
    // if (atRiskRadioValue !== data.atRiskState) {
    riskSeverityMutate();
    // }
    setIsEditing(false);
  };

  const handleOnAtRiskStateChange = (_event, value) => {
    setAtRiskRadioValue(value);
  };

  const {userRoles} = usePermissions(config);
  return (
    <EditableFieldWrapper {...restProps}>
      <EditableField handleOnMouseEnter={handleOnMouseEnterRisk} handleOnMouseLeave={handleOnMouseLeaveRisk} label={'patient.card.atRisk'}>
        {isEditing ? (
          // TODO-JK: change enum name add eventHandler, selected value
          <DlMuiRadioGroup
            options={atRiskRadioGroupOptions}
            selectedValue={atRiskRadioValue}
            handleSave={handleOnSubmitRisk}
            handleDiscard={handleDiscardRisk}
            handleOnChange={handleOnAtRiskStateChange}
          />
        ) : (
          <EditIconWrapper
            showEditButton={showEditButtonRisk && userRoles.ENCOUNTER_DETAIL_EDIT_ROLE}
            handleOnClick={handleOnClickRisk}
            showAlertIcon={data.atRiskState === 'AT_RISK'}
            alertIconTitle={'patient.card.chip.atRiskAlert'}
            data-name='at-risk-content'
          >
            <DlMuiTypography variant={'value'} value={tEnum('RiskSeverityEnum', data.atRiskState)} />
          </EditIconWrapper>
        )}
      </EditableField>
      {changedByOtherDialog}
    </EditableFieldWrapper>
  );
};

AtRiskEdit.displayName = 'AtRiskEdit';

const getStyles: GetStylesFunction = (_options) => ({});
